import React, { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { axiosInstance } from '../../../axiosApi';
import { AnalyticsEvents, trackEvent } from '../../utils/analytics';
import { publicAccent, publicCard, publicLine, publicText } from './publicColors';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  width: 100%;
  max-width: 28rem;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

const Primary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  text-decoration: none;
  border-radius: 0.5rem;
  padding: 0.9rem 1.25rem;
  font-weight: 600;
  font-size: 1rem;
`;

const Secondary = styled(Link)`
  color: ${publicAccent};
  font-weight: 600;
`;

const Quiet = styled(Link)`
  color: ${publicText};
  opacity: 0.75;
  font-size: 0.95rem;
`;

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  width: 100%;
`;

const Label = styled.label`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

const Input = styled.input`
  flex: 1 1 14rem;
  background: ${publicCard};
  border: 1px solid ${publicLine};
  border-radius: 0.5rem;
  padding: 0.9rem 1rem;
  color: ${publicText};
  font-size: 1rem;
`;

const Submit = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 0.5rem;
  color: #fff;
  padding: 0.9rem 1.25rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const Note = styled.p`
  margin: 0;
  color: ${publicText};
  font-size: 0.95rem;
`;

type GetStartedProps = {
  source: 'landing' | 'pricing';
  idPrefix?: string;
  showPricingLink?: boolean;
};

/**
 * Create account when registration is on. Email waitlist when it is off or unknown.
 * Settings default to off in the API, so a failed lookup stays on the waitlist.
 */
const GetStarted = ({
  source,
  idPrefix = 'waitlist',
  showPricingLink = true,
}: GetStartedProps): JSX.Element => {
  const [registrationEnabled, setRegistrationEnabled] = useState<boolean | null>(null);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get('/public/settings/')
      .then((response) => {
        if (cancelled) return;
        setRegistrationEnabled(Boolean(response.data?.enable_account_registration));
      })
      .catch(() => {
        if (!cancelled) setRegistrationEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onCreateAccount = () => {
    trackEvent(AnalyticsEvents.LANDING_PRIMARY_CTA, { source, action: 'create_account' });
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter an email address.');
      setMessage(null);
      return;
    }
    setSubmitting(true);
    setError(null);
    setMessage(null);
    try {
      await axiosInstance.post('/public/waitlist/', { email: trimmed });
      trackEvent(AnalyticsEvents.WAITLIST_JOINED, { source });
      setMessage("You're on the list.");
      setEmail('');
    } catch {
      setError('Could not join the waitlist. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (registrationEnabled === null) {
    return <Note>Checking signup…</Note>;
  }

  if (registrationEnabled) {
    return (
      <Stack>
        <Row>
          <Primary to="/signup/" onClick={onCreateAccount}>
            Create account
          </Primary>
          {showPricingLink && (
            <Secondary
              to="/pricing/"
              onClick={() => trackEvent(AnalyticsEvents.LANDING_PRICING_CTA, { source })}
            >
              See pricing
            </Secondary>
          )}
        </Row>
        <Quiet to="/signin/">Sign in</Quiet>
      </Stack>
    );
  }

  return (
    <Stack>
      <Form onSubmit={onSubmit}>
        <Label htmlFor={`${idPrefix}-email`}>Email</Label>
        <Input
          id={`${idPrefix}-email`}
          name="email"
          type="email"
          autoComplete="email"
          placeholder="Email address"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <Submit type="submit" disabled={submitting}>
          {submitting ? 'Joining…' : 'Join the waitlist'}
        </Submit>
      </Form>
      {message && <Note role="status">{message}</Note>}
      {error && <Note role="alert">{error}</Note>}
      <Row>
        {showPricingLink && (
          <Secondary
            to="/pricing/"
            onClick={() => trackEvent(AnalyticsEvents.LANDING_PRICING_CTA, { source })}
          >
            See pricing
          </Secondary>
        )}
        <Quiet to="/signin/">Sign in</Quiet>
      </Row>
    </Stack>
  );
};

export default GetStarted;
